import { useState } from 'react';
import type { JSX } from 'react';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { FeedbackPalette } from '../../types.js';

interface StarRatingProps {
  palette: FeedbackPalette;
  value: number | null;
  onChange: (rating: number) => void;
}

// One easing curve shared across rating widgets so a star, an emoji and a
// thumbs button all move in step — small detail but it's what makes the
// kit feel like a single design system instead of three separate widgets.
const RATING_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

export function StarRating({ palette, value, onChange }: StarRatingProps): JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);
  // Hover paints a preview using a 60%-alpha accent (the same tier the
  // survey ChoiceRow/EmojiScale use for hover) so the user gets a clear
  // "this is what you'd land on" cue. Click commits the full accent.
  const previewBoundary = hovered ?? value ?? 0;

  return (
    <div
      style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: '8px 0' }}
      onMouseLeave={() => setHovered(null)}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const isCommitted = value !== null && star <= value;
        const isPreview = hovered !== null && star <= hovered && !isCommitted;
        const fill = isCommitted
          ? palette.primary
          : isPreview
            ? hexWithAlpha(palette.primary, 0.35)
            : 'transparent';
        const stroke = star <= previewBoundary ? palette.primary : palette.textMuted;
        return (
          <button
            key={star}
            type="button"
            aria-label={`Rate ${star}`}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              // 2 px lift on hover; selected stays flat so a committed
              // answer doesn't continue to move.
              transform: hovered === star && value !== star ? 'translateY(-2px)' : 'translateY(0)',
              transition: `transform 220ms ${RATING_EASE}`,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="38"
              height="38"
              fill={fill}
              stroke={stroke}
              strokeWidth="1.5"
              style={{
                transition: `fill 220ms ${RATING_EASE}, stroke 220ms ${RATING_EASE}`,
              }}
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        );
      })}
    </div>
  );
}
